/**
 * Image URL utilities for handling CDN and proxy URLs
 */

// Cache the CDN domain after fetching
let cachedCdnDomain: string | null = null;
let cdnDomainFetched = false;

/**
 * Fetch the CDN domain from the backend config API
 * This is called once on app initialization
 */
export async function fetchCdnConfig(): Promise<string | null> {
    if (cdnDomainFetched) {
        return cachedCdnDomain;
    }

    try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/config`);
        if (response.ok) {
            const config = await response.json();
            cachedCdnDomain = config.cdnDomain || null;
            console.log('[CDN] Fetched CDN config:', cachedCdnDomain ? `CDN enabled: ${cachedCdnDomain}` : 'CDN not configured');
        }
    } catch (error) {
        console.warn('[CDN] Failed to fetch CDN config, using proxy URLs:', error);
    }

    cdnDomainFetched = true;
    return cachedCdnDomain;
}

/**
 * Get the cached CDN domain (synchronous)
 * Returns null if CDN is not configured or not yet fetched
 */
export function getCdnDomain(): string | null {
    return cachedCdnDomain;
}

/**
 * Set the CDN domain manually (for testing or direct configuration)
 */
export function setCdnDomain(domain: string | null): void {
    cachedCdnDomain = domain;
    cdnDomainFetched = true;
}

/**
 * Resolve an image URL to the best available source
 * - If it's already a full URL (https://...), return as-is
 * - If CDN is configured and it's an object path, return CDN URL
 * - Otherwise, prefix with API base URL for proxy serving
 * 
 * @param imageUrl The image URL from the database
 * @param fallbackImage Optional fallback image if imageUrl is empty
 */
export function resolveImageUrl(imageUrl: string | null | undefined, fallbackImage?: string): string {
    // If no image URL, use fallback
    if (!imageUrl) {
        return fallbackImage || '';
    }

    // If it's already a full URL, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
    }

    // If it's an object path and CDN is configured, use CDN
    if (cachedCdnDomain && (imageUrl.startsWith('/api/objects/') || imageUrl.startsWith('/objects/'))) {
        // Extract the S3 key from the path
        const key = imageUrl
            .replace('/api/objects/', '')
            .replace('/objects/', '');
        return `https://${cachedCdnDomain}/${key}`;
    }

    // If it starts with /api/ or /objects/, use the API base URL
    if (imageUrl.startsWith('/api/') || imageUrl.startsWith('/objects/')) {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        return `${apiBaseUrl}${imageUrl}`;
    }

    // If it starts with /, it's likely a local asset
    if (imageUrl.startsWith('/')) {
        return imageUrl;
    }

    // Otherwise, assume it's a stock image filename
    return `/assets/stock_images/${imageUrl}`;
}
